import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Lock, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, {
        token,
        new_password: password
      }, { timeout: 15000 });
      setSuccess(true);
    } catch (err) {
      // If it's a network error (timeout, no response), the password may have been reset
      if (!err.response) {
        setError('Network issue — but your password may have been updated. Try signing in with your new password.');
      } else {
        setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
      }
    } finally {
      setLoading(false);
    }
  };

  // No token in URL
  if (!token) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center px-4" style={{ backgroundColor: 'var(--background-main)' }}>
        <Card className="w-full max-w-md bg-[var(--background-surface)] border-[var(--border-subtle)]">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-[#FF3B30] mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold text-[var(--text-primary)] mb-2">Invalid Reset Link</h2>
            <p className="text-[var(--text-secondary)] mb-4">
              This password reset link is invalid or has expired.
            </p>
            <Link to="/forgot-password">
              <Button className="bg-[#8B5A3C] hover:bg-[#A46944] text-white">
                Request New Link
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4" style={{ backgroundColor: 'var(--background-main)' }}>
      <Card className="w-full max-w-md bg-[var(--background-surface)] border-[var(--border-subtle)]">
        <CardHeader className="text-center">
          <Link to="/" className="inline-block mb-4">
            <h1 className="font-heading text-2xl font-bold text-[var(--text-primary)]">Sudoku Academy</h1>
          </Link>
          <CardTitle className="text-2xl font-heading text-[var(--text-primary)]">
            {success ? 'Password Reset!' : 'Set New Password'}
          </CardTitle>
          <CardDescription className="text-[var(--text-secondary)]">
            {success 
              ? 'Your password has been updated successfully.'
              : 'Enter your new password below.'
            }
          </CardDescription>
        </CardHeader>
        <CardContent>
          {success ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded bg-[#34C759]/10 border border-[#34C759]/20" data-testid="reset-success">
                <CheckCircle className="w-5 h-5 text-[#34C759] flex-shrink-0" />
                <p className="text-sm text-[var(--text-primary)]">
                  You can now sign in with your new password.
                </p>
              </div>
              <Button
                onClick={() => navigate('/login')}
                className="w-full bg-[#8B5A3C] hover:bg-[#A46944] text-white font-semibold"
                data-testid="go-to-login"
              >
                Go to Sign In
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <div className="flex items-center gap-2 p-3 rounded bg-red-500/10 border border-red-500/20 text-red-400 text-sm" data-testid="reset-error">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[var(--text-secondary)]">New Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-disabled)]" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter new password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-[var(--background-elevated)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-disabled)]"
                    required
                    minLength={6}
                    data-testid="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-disabled)] hover:text-[var(--text-secondary)] transition-colors"
                    tabIndex={-1}
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm-password" className="text-[var(--text-secondary)]">Confirm Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--text-disabled)]" />
                  <Input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="pl-10 bg-[var(--background-elevated)] border-[var(--border-subtle)] text-[var(--text-primary)] placeholder:text-[var(--text-disabled)]"
                    required
                    minLength={6}
                    data-testid="confirm-password"
                  />
                </div>
                <p className="text-xs text-[var(--text-disabled)]">Minimum 6 characters</p>
              </div>

              <Button
                type="submit"
                className="w-full bg-[#8B5A3C] hover:bg-[#A46944] text-white font-semibold"
                disabled={loading}
                data-testid="reset-submit"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Resetting...
                  </span>
                ) : (
                  'Reset Password'
                )}
              </Button>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
